/**
 * Block "materials" for plans: which texture shows on top / on the side of a block, so a path can be
 * drawn as gravel or a wall as stone bricks. Works for every block in the loaded jars (modded too).
 */
import type { ModelBaker, TintKind } from "./models.ts";

export interface BlockMaterial {
  id: string; // "minecraft:gravel"
  name: string; // "Gravel"
  /** texture layers in the block texture array */
  top: number;
  side: number;
  /** biome tint of the top/side faces (grass, leaves, water) — drawn with a default colour */
  tint: TintKind;
  tintTop: boolean;
  tintSide: boolean;
  /** has a (nearly) full top face → good for paths/areas */
  surface: boolean;
}

/** Default biome colours used when a tinted texture is drawn outside the world (plains). */
export const DEFAULT_TINT: Partial<Record<TintKind, number>> = {
  grass: 0x91bd59,
  foliage: 0x77ab2f,
  dry_foliage: 0xa0a069,
  water: 0x3f76e4,
};

/** "minecraft:dirt_path" → "Dirt Path", "create:andesite_casing" → "Andesite Casing (create)" */
export function blockDisplayName(id: string): string {
  const [ns, path] = id.includes(":") ? id.split(":", 2) : ["minecraft", id];
  const name = path.split("/").pop()!.split("_").map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w)).join(" ");
  return ns === "minecraft" ? name : `${name} (${ns})`;
}

/** Properties of the "default" variant: first variant key, preferring an upright one (logs: axis=y). */
function defaultProps(blockstate: unknown): Record<string, string> {
  const bs = blockstate as { variants?: Record<string, unknown> } | undefined;
  const keys = bs?.variants ? Object.keys(bs.variants) : [];
  // crops: the fully grown stage (highest age), so a wheat field looks like wheat
  const age = (k: string) => Number(/(^|,)age=(\d+)/.exec(k)?.[2] ?? -1);
  const grown = keys.filter((k) => age(k) >= 0).sort((a, b) => age(b) - age(a))[0];
  const key = grown ?? keys.find((k) => /(^|,)axis=y(,|$)/.test(k)) ?? keys.find((k) => /(^|,)(type=bottom|half=bottom)(,|$)/.test(k)) ?? keys[0] ?? "";
  const props: Record<string, string> = {};
  if (key && key !== "normal") for (const kv of key.split(",")) { const [k, v] = kv.split("="); if (k) props[k] = v ?? ""; }
  return props;
}

const cache = new WeakMap<ModelBaker, Map<string, BlockMaterial | null>>();

/** Top/side textures of a block, or null if the block has no model. */
export function blockMaterial(baker: ModelBaker, id: string): BlockMaterial | null {
  let per = cache.get(baker);
  if (!per) { per = new Map(); cache.set(baker, per); }
  if (per.has(id)) return per.get(id)!;
  let out: BlockMaterial | null = null;
  const bs = baker.bundle.blockstates[id];
  const fluid = id === "minecraft:water" ? "water" : id === "minecraft:lava" ? "lava" : null;
  if (fluid) {
    const still = baker.textureLayer(`minecraft:block/${fluid}_still`);
    const flow = baker.textureLayer(`minecraft:block/${fluid}_flow`);
    out = { id, name: blockDisplayName(id), top: still, side: flow || still, tint: fluid === "water" ? "water" : "none", tintTop: fluid === "water", tintSide: fluid === "water", surface: true };
  } else if (bs) {
    const props = defaultProps(bs);
    const m = baker.bake(`material|${id}|${JSON.stringify(props)}`, { name: id, props });
    if (!m.unknown && m.quads.length) {
      const area = (q: (typeof m.quads)[number]) => {
        // bounding area of the quad in the plane of its face (good enough to pick the biggest)
        const p = q.pos;
        const xs = [p[0], p[3], p[6], p[9]], ys = [p[1], p[4], p[7], p[10]], zs = [p[2], p[5], p[8], p[11]];
        const span = (a: number[]) => Math.max(...a) - Math.min(...a);
        return Math.max(span(xs) * span(zs), span(xs) * span(ys), span(ys) * span(zs));
      };
      // flat quads by axis: the highest horizontal one is the top, the most northern vertical one the side
      const flat = (axis: number) => m.quads.filter((q) => q.pos[axis] === q.pos[axis + 3] && q.pos[axis] === q.pos[axis + 6] && q.pos[axis] === q.pos[axis + 9]);
      const tops = flat(1).sort((a, b) => b.pos[1] - a.pos[1] || area(b) - area(a));
      const sides = flat(2).sort((a, b) => a.pos[2] - b.pos[2] || area(b) - area(a));
      const byArea = m.quads.slice().sort((a, b) => area(b) - area(a));
      const up = tops[0] ?? byArea[0];
      const side = sides[0] ?? flat(0)[0] ?? up;
      const upFull = !!tops[0] && area(tops[0]) > 0.8 && tops[0].pos[1] > 0.85;
      out = {
        id,
        name: blockDisplayName(id),
        top: up.tex,
        side: side.tex,
        tint: m.tint,
        tintTop: up.tint >= 0,
        tintSide: side.tint >= 0,
        surface: upFull || !!m.fluid,
      };
    }
  }
  per.set(id, out);
  return out;
}

/** Every block with a model; surface blocks first, then by name. */
export function listBlockMaterials(baker: ModelBaker): BlockMaterial[] {
  const out: BlockMaterial[] = [];
  for (const id of Object.keys(baker.bundle.blockstates)) {
    const m = blockMaterial(baker, id);
    if (m) out.push(m);
  }
  return out.sort((a, b) => Number(b.surface) - Number(a.surface) || a.name.localeCompare(b.name));
}

/** Suggested materials per plan kind (only those present in the loaded jar are shown). */
export const MATERIAL_FAVOURITES: Record<"path" | "area" | "building", string[]> = {
  path: [
    "minecraft:dirt_path", "minecraft:gravel", "minecraft:cobblestone", "minecraft:stone_bricks", "minecraft:mossy_cobblestone",
    "minecraft:smooth_stone", "minecraft:polished_andesite", "minecraft:packed_mud", "minecraft:mud_bricks", "minecraft:bricks",
    "minecraft:deepslate_tiles", "minecraft:oak_planks", "minecraft:spruce_planks", "minecraft:sand", "minecraft:coarse_dirt",
  ],
  area: [
    "minecraft:grass_block", "minecraft:farmland", "minecraft:water", "minecraft:sand", "minecraft:podzol", "minecraft:moss_block",
    "minecraft:coarse_dirt", "minecraft:snow_block", "minecraft:stone", "minecraft:oak_planks", "minecraft:gravel", "minecraft:mud",
  ],
  building: [
    "minecraft:oak_planks", "minecraft:spruce_planks", "minecraft:dark_oak_planks", "minecraft:stone_bricks", "minecraft:cobblestone",
    "minecraft:bricks", "minecraft:deepslate_bricks", "minecraft:sandstone", "minecraft:white_concrete", "minecraft:quartz_block",
    "minecraft:oak_log", "minecraft:glass", "minecraft:mud_bricks", "minecraft:terracotta",
  ],
};
